import { useState, useEffect, useRef, useCallback } from "react";
import { adaiApi } from "../api/adaiApi.js";
import {
  HISTORY_DEBOUNCE_MS, HISTORY_PAGE_SIZE, HISTORY_POLL_INTERVAL_MS,
  HISTORY_POLLING_STATUSES,
} from "../utils/constants.js";
import { useWorkspace } from "../../context/WorkspaceContext";

export { HISTORY_PAGE_SIZE as PAGE_SIZE };

export function useHistoryData() {
  const { currentEngagementId } = useWorkspace();
  const [records, setRecords]       = useState([]);
  const [total, setTotal]           = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState("");
  const [sortOrder, setSortOrder]   = useState("desc");
  const [status, setStatus]         = useState("all");
  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(HISTORY_PAGE_SIZE);

  const debounceRef                       = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, HISTORY_DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  useEffect(() => { setPage(1); }, [sortOrder]);

  useEffect(() => { setPage(1); }, [status]);

  useEffect(() => { setPage(1); }, [pageSize]);

  useEffect(() => { setPage(1); }, [currentEngagementId]);

  const fetchRecords = useCallback(async () => {
    if (!currentEngagementId) {
      setRecords([]);
      setTotal(0);
      setTotalPages(1);
      return;
    }
    try {
      const { data } = await adaiApi.listJobs({
        engagementId: currentEngagementId,
        search:   debouncedSearch || undefined,
        sortOrder,
        status:   status !== "all" ? status : undefined,
        page,
        pageSize,
      });
      setRecords(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch {
      // silently ignore — stale data shown instead
    }
  }, [currentEngagementId, debouncedSearch, sortOrder, status, page, pageSize]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        await fetchRecords();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [fetchRecords]);

  const hasActiveJobs = records.some((r) => HISTORY_POLLING_STATUSES.has(r.status));
  useEffect(() => {
    if (!hasActiveJobs) return;
    const interval = setInterval(fetchRecords, HISTORY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasActiveJobs, fetchRecords]);

  return {
    records, total, totalPages, loading,
    search, setSearch,
    sortOrder, setSortOrder,
    status, setStatus,
    page, setPage,
    pageSize, setPageSize,
  };
}
